import mixpanel from 'mixpanel-browser';
import { memo, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import shajs from 'sha.js';

import {
  GET_MIXPANEL_MOCK,
  MixpanelContext,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelMetadata,
  isUserProdMon,
  useDefaultMarketAccounts,
  useFavoriteSecurities,
  useUserContext,
  type DeviceType,
  type MixpanelEventSource,
  type MixpanelInstance,
} from '@talos/kyoko';
import type { GroupPanelViewState, SerializedGridObject } from 'dockview';
import { isArray, isObject, keys } from 'lodash-es';
import {
  useLayoutSpecApiAccess,
  useLayoutSpecsQuery,
  type LayoutSpecResponse,
  type LayoutSpecShareResponse,
} from '../components/AppLayout/LayoutSpecs/LayoutSpecSlice';
import { useMarketSelectorConfig } from '../components/MarketSelector/MarketSelectorConfigContext';
import { envConfig } from '../utils/envConfig';
import { getDisplayMode } from '../utils/getDisplayMode';
import { getDisplayModeQuery } from '../utils/getDisplayModeQuery';
import { useAppLayoutConfig } from './AppLayoutConfig/AppLayoutConfigContext';
import { useDefaultAggregation } from './DefaultAggregationContext';
import { useDisplaySettings } from './DisplaySettingsProvider';
import { useFavoriteAggregations } from './FavoriteAggregationsContext';
import { useOrderPresets } from './OrderPresetsContext';
import { useTradingSettings } from './TradingSettingsContext';

const hash = (value: string) => shajs('sha1').update(value).digest('hex');

/**
 * Count the number of leaf nodes in a dockview layout grid structure
 */
function getNumberOfLeafNodes(layoutGroup: SerializedGridObject<GroupPanelViewState>): number {
  if (layoutGroup.type === 'leaf') {
    return 1;
  } else if (layoutGroup.type === 'branch') {
    // Cast to get around type not being narrowed by checking the type property
    const data = layoutGroup.data as SerializedGridObject<GroupPanelViewState>[];
    return data.reduce((count, item) => count + getNumberOfLeafNodes(item), 0);
  }
  return 0;
}

type TemplateAccessType = 'public' | 'private' | 'roles' | 'userGroups' | 'users';
type ShareTargetAccessType = 'roles' | 'userGroups' | 'users';

function getAccessType(template: LayoutSpecResponse): TemplateAccessType {
  if (template.IsPublic) {
    return 'public';
  }

  const shares = template.Shares ?? [];
  if (shares.length === 0) {
    return 'private';
  }

  const shareTypes = new Set<ShareTargetAccessType>();
  for (const share of shares as LayoutSpecShareResponse[]) {
    if ('Role' in share && share.Role != null) {
      shareTypes.add('roles');
    }
    if ('GroupID' in share && share.GroupID != null) {
      shareTypes.add('userGroups');
    }
    if ('User' in share && share.User != null) {
      shareTypes.add('users');
    }
  }

  if (shareTypes.size === 0) {
    return 'private';
  }

  return Array.from(shareTypes)[0];
}

export const MixpanelProvider = memo(function MixpanelProvider(props: { children: ReactNode }) {
  const { user } = useUserContext();
  const { omsPopoutGroups } = useTradingSettings();
  const { showAllInPrices, showFirmLiquidity, showDelta1BPS, showSidebarLabels } = useDisplaySettings();
  const {
    enableGroups,
    allowSyntheticCcy,
    useTradeAllocations,
    defaultSubAccountId,
    allowedSlippage,
    clickToTradeAll,
    alwaysShowBestBidOffer,
    defaultToEmptyOrderMarkets,
    useNewMarketSelector,
    resetOrderAccountsOnSubAccountChange,
    newMultilegDefaultDelta1Behaviour,
  } = useTradingSettings();

  const userIsProdMon = isUserProdMon(user);

  const { favoriteSecurities } = useFavoriteSecurities();
  const { favoriteAggregations } = useFavoriteAggregations();
  const { defaultAggregationId } = useDefaultAggregation();
  const { defaultMarketAccounts } = useDefaultMarketAccounts();
  const { canListAllSpaces } = useLayoutSpecApiAccess();
  const { data: layoutSpecs } = useLayoutSpecsQuery();

  const getInstance = useCallback((source?: MixpanelEventSource | undefined): MixpanelInstance => {
    if (envConfig.VITE_AVA_ENV === 'prod' || envConfig.VITE_AVA_ENV === 'sandbox') {
      return {
        set_group: function (...args) {
          mixpanel.set_group(...args);
        },
        remove_group: function (...args) {
          mixpanel.remove_group(...args);
        },
        add_group: function (...args) {
          mixpanel.add_group(...args);
        },
        identify: function (...args) {
          mixpanel.identify(...args);
        },
        register: function (...args) {
          mixpanel.register(...args);
        },
        time_event: function (...args) {
          mixpanel.time_event(...args);
        },
        opt_in_tracking: function (...args) {
          mixpanel.opt_in_tracking(...args);
        },
        opt_out_tracking: function (...args) {
          mixpanel.opt_out_tracking(...args);
        },
        people: mixpanel.people,
        track: function (...args) {
          const [, properties = {}] = args;
          if (source && !properties[MixpanelEventProperty.Source]) {
            properties[MixpanelEventProperty.Source] = source;
          }

          args[1] = { ...properties, windowWidth: window.innerWidth, windowHeight: window.innerHeight };
          mixpanel.track(...args);
        },
      };
    }
    return GET_MIXPANEL_MOCK(source);
  }, []);

  const internalInstance = useMemo(() => getInstance(), [getInstance]);

  useEffect(() => {
    if (internalInstance && user) {
      internalInstance.identify(hash(`${user.ID}_${user.Organization}_${envConfig.VITE_AVA_ENV}`).substring(0, 6));
      internalInstance.register({ [MixpanelMetadata.Organization]: user.Organization });
      internalInstance.people.set({ [MixpanelMetadata.Organization]: user.Organization });
      internalInstance.register({
        [MixpanelMetadata.Environment]: envConfig.VITE_AVA_ENV,
        [MixpanelMetadata.LastSeen]: new Date().toISOString(),
        [MixpanelMetadata.Release]: import.meta.env.VITE_GIT_HASH,
        [MixpanelMetadata.Version]: import.meta.env.VITE_NPM_PACKAGE_VERSION,
      });
    }
  }, [internalInstance, user]);

  useEffect(() => {
    const roles = user?.Roles ?? [];
    if (internalInstance && user) {
      for (const role of roles) {
        internalInstance.add_group('role', role);
      }

      if (userIsProdMon) {
        internalInstance.add_group('talos_user', 'prodmon');
      }
      return () => {
        for (const role of roles) {
          internalInstance.remove_group('role', role);
        }
        internalInstance.remove_group('talos_user', 'prodmon');
      };
    }
  }, [internalInstance, user, userIsProdMon]);

  // Track count of tabs for each blotter
  useEffect(() => {
    if (internalInstance && user.AppConfig) {
      const appConfigJSON = JSON.parse(user.AppConfig);

      if (isObject(appConfigJSON)) {
        if ('tabs' in appConfigJSON && isObject(appConfigJSON.tabs)) {
          const tabsObj = appConfigJSON.tabs;
          const keyNumberOfTabsPairObject: Record<string, number> = {};
          for (const [key, tabObj] of Object.entries(tabsObj)) {
            if (isObject(tabObj) && 'items' in tabObj && isArray(tabObj.items)) {
              const numberOfTabs = tabObj.items.length;
              keyNumberOfTabsPairObject[key] = numberOfTabs;
            }
          }
          internalInstance.register(keyNumberOfTabsPairObject);
        }
      }
    }
  }, [internalInstance, user.AppConfig]);

  const { layoutConfigs: tradingLayouts } = useAppLayoutConfig('trading');
  const { layoutConfigs: monitoringLayouts } = useAppLayoutConfig('monitoring');
  useEffect(() => {
    const tradingLayoutList = tradingLayouts ?? [];
    const monitoringLayoutList = monitoringLayouts ?? [];
    const allLayouts = [...tradingLayoutList, ...monitoringLayoutList];
    const numberOfSpacesByDeviceType: Record<DeviceType, number> = {
      all: 0,
      desktop: 0,
      laptop: 0,
      mobile: 0,
      tablet: 0,
      widescreen: 0,
    };

    allLayouts.forEach(layout => {
      const deviceType = layout.deviceType ?? 'all';
      numberOfSpacesByDeviceType[deviceType] += 1;
    });

    let panels = 0;
    let layoutPopouts = 0;
    let groups = 0;
    tradingLayoutList.forEach(layout => {
      const panelsList = Object.values(layout.dockViewLayout?.panels ?? {});
      panels += panelsList.length;
      layoutPopouts += Object.values(layout.dockViewLayout?.popoutGroups ?? {}).length;
      groups += getNumberOfLeafNodes(layout.dockViewLayout?.grid?.root ?? {});
    });
    internalInstance.register({
      [MixpanelMetadata.NumberOfMonitoringSpaces]: monitoringLayoutList.length,
      [MixpanelMetadata.NumberOfSpaces]: allLayouts.length,
      [MixpanelMetadata.NumberOfSpacesAllDevices]: numberOfSpacesByDeviceType.all,
      [MixpanelMetadata.NumberOfSpacesDesktop]: numberOfSpacesByDeviceType.desktop,
      [MixpanelMetadata.NumberOfSpacesLaptop]: numberOfSpacesByDeviceType.laptop,
      [MixpanelMetadata.NumberOfSpacesMobile]: numberOfSpacesByDeviceType.mobile,
      [MixpanelMetadata.NumberOfSpacesTablet]: numberOfSpacesByDeviceType.tablet,
      [MixpanelMetadata.NumberOfSpacesWidescreen]: numberOfSpacesByDeviceType.widescreen,
      [MixpanelMetadata.NumberOfTradingSpaces]: tradingLayoutList.length,
      [MixpanelMetadata.NumberOfTradingLayouts]: tradingLayoutList.length,
      [MixpanelMetadata.NumberOfTradingLayoutPanels]: panels,
      [MixpanelMetadata.NumberOfTradingLayoutPopouts]: layoutPopouts,
      [MixpanelMetadata.NumberOfTradingLayoutGroups]: groups,
    });
  }, [internalInstance, tradingLayouts, monitoringLayouts]);

  useEffect(() => {
    const omsPopouts = Object.values(omsPopoutGroups ?? {});
    internalInstance.register({
      [MixpanelMetadata.NumberOfOMSPopouts]: omsPopouts.length,
    });
  }, [internalInstance, omsPopoutGroups]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.ShowAllInPrices]: !!showAllInPrices,
        [MixpanelMetadata.FirmLiquidity]: !!showFirmLiquidity,
        [MixpanelMetadata.Delta1BPS]: !!showDelta1BPS,
      });
  }, [internalInstance, showAllInPrices, showFirmLiquidity, showDelta1BPS]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.ShowSidebarLabels]: !!showSidebarLabels,
      });
  }, [internalInstance, showSidebarLabels]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.AllowedSlippage]: allowedSlippage?.value,
        [MixpanelMetadata.EnableClickToTrade]: clickToTradeAll,
        [MixpanelMetadata.EnableOrderGroups]: enableGroups,
        [MixpanelMetadata.EnableSyntheticCurrency]: allowSyntheticCcy,
        [MixpanelMetadata.UseSubaccounts]: !useTradeAllocations && !!defaultSubAccountId,
        [MixpanelMetadata.UseTradeAllocations]: useTradeAllocations,
        [MixpanelMetadata.AlwaysDisplayBestBidOffer]: alwaysShowBestBidOffer,
        [MixpanelMetadata.BlankMarketAccountsWhenOpeningOrderForm]: defaultToEmptyOrderMarkets,
        [MixpanelMetadata.EnableNewMarketAccountSelector]: useNewMarketSelector,
        [MixpanelMetadata.ResetOrderAccountsOnSubAccountChange]: resetOrderAccountsOnSubAccountChange,
        [MixpanelMetadata.NewMultilegDefaultDelta1Behaviour]: newMultilegDefaultDelta1Behaviour,
      });
  }, [
    internalInstance,
    enableGroups,
    allowSyntheticCcy,
    useTradeAllocations,
    defaultSubAccountId,
    allowedSlippage,
    clickToTradeAll,
    alwaysShowBestBidOffer,
    defaultToEmptyOrderMarkets,
    useNewMarketSelector,
    resetOrderAccountsOnSubAccountChange,
    newMultilegDefaultDelta1Behaviour,
  ]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.NumberOfFavoriteSymbols]: favoriteSecurities ? favoriteSecurities.length : 0,
      });
  }, [internalInstance, favoriteSecurities]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.NumberOfFavoriteAggregations]: favoriteAggregations ? favoriteAggregations.length : 0,
      });
  }, [internalInstance, favoriteAggregations]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.HasDefaultAggregation]: Boolean(defaultAggregationId),
      });
  }, [internalInstance, defaultAggregationId]);

  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.NumberOfDefaultMarketAccounts]: defaultMarketAccounts
          ? keys(defaultMarketAccounts).length
          : 0,
      });
  }, [internalInstance, defaultMarketAccounts]);

  const { orderPresetsList } = useOrderPresets();
  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.NumberOfOrderPresets]: orderPresetsList?.length ?? 0,
      });
  }, [internalInstance, orderPresetsList]);

  const { selectionListShowAll } = useMarketSelectorConfig();
  useEffect(() => {
    internalInstance &&
      internalInstance.register({
        [MixpanelMetadata.MarketSelectorShowAll]: selectionListShowAll,
      });
  }, [internalInstance, selectionListShowAll]);

  useEffect(() => {
    if (!internalInstance) {
      return;
    }

    const numberOfTemplatesByAccessType: Record<TemplateAccessType, number> = {
      private: 0,
      public: 0,
      roles: 0,
      userGroups: 0,
      users: 0,
    };

    const templateCounts = (layoutSpecs ?? []).reduce(
      (counts, spec) => {
        counts.total += 1;
        numberOfTemplatesByAccessType[getAccessType(spec)] += 1;
        if (spec.Config.availability === 'all') {
          counts.allUsers += 1;
        }
        if (spec.Config.availability === 'new-users') {
          counts.newUsers += 1;
        }
        return counts;
      },
      { allUsers: 0, newUsers: 0, total: 0 }
    );

    internalInstance.register({
      [MixpanelMetadata.NumberOfDefaultTemplatesForAllUsers]: templateCounts.allUsers,
      [MixpanelMetadata.NumberOfDefaultTemplatesForNewUsers]: templateCounts.newUsers,
      [MixpanelMetadata.NumberOfTemplatesPrivate]: numberOfTemplatesByAccessType.private,
      [MixpanelMetadata.NumberOfTemplatesPublic]: numberOfTemplatesByAccessType.public,
      [MixpanelMetadata.NumberOfTemplatesGroupShared]: numberOfTemplatesByAccessType.userGroups,
      [MixpanelMetadata.NumberOfTemplatesRoleShared]: numberOfTemplatesByAccessType.roles,
      [MixpanelMetadata.NumberOfTemplatesUserShared]: numberOfTemplatesByAccessType.users,
      [MixpanelMetadata.NumberOfTemplates]: templateCounts.total,
      [MixpanelMetadata.TemplatesCountScope]: canListAllSpaces ? 'org' : 'user',
    });
  }, [internalInstance, layoutSpecs, canListAllSpaces]);

  useEffect(() => {
    if (!internalInstance || !window.matchMedia) {
      return;
    }
    const displayMode = getDisplayMode();
    const { query } = getDisplayModeQuery();

    internalInstance.register({
      [MixpanelMetadata.DisplayMode]: displayMode,
    });

    const displayModeChangeCallback = function (e: MediaQueryListEvent) {
      const displayMode = e.matches ? 'standalone' : 'browser';
      internalInstance.track(MixpanelEvent.ChangeDisplayMode, {
        [MixpanelEventProperty.DisplayMode]: displayMode,
      });
    };
    query.addEventListener('change', displayModeChangeCallback);

    const appInstalledCallback = () => {
      internalInstance.track(MixpanelEvent.WebAppInstalled);
    };
    window.addEventListener('appinstalled', appInstalledCallback);
    return () => {
      query.removeEventListener('change', displayModeChangeCallback);
      window.removeEventListener('appinstalled', appInstalledCallback);
    };
  }, [internalInstance]);

  return <MixpanelContext.Provider value={getInstance}>{props.children}</MixpanelContext.Provider>;
});
